import { mapDTO } from '@twistezo/ts-dto-mapper'
import _ from 'lodash'
import { PartialDeep } from 'type-fest'

import {
  BiddersInput,
  PrebidModuleInput,
} from 'features/ab-tests/forms/AbTestsCommonForm/abTestsCommonSchema'
import { fieldsToOmit } from 'providers/tanstack-query/utils'
import { floatPercentToDTO, secondsToMilliseconds } from 'utils/number'
import { CreateAbTestInputData } from '../types'
import {
  AbTestVariantAttributesDTO,
  PrebidBidderInputDTO,
  PrebidModuleInputDTO,
  Variables,
} from './types'

type From = CreateAbTestInputData & {
  withYp2395: boolean
}

type To = Variables

export const toDTO = (from: From): To =>
  mapDTO<From, To>({ from }).transform((from: From): To => {
    const { cookieLifetime, domainId, name, status, trafficSplitType, variants, withYp2395 } = from

    const constantFields: Pick<Variables, 'domainId' | 'name' | 'status' | 'variants'> = {
      domainId,
      name,
      status,
      variants: variantsToDTO(variants),
    }

    const variableFields: Pick<Variables, 'cookieLifetime' | 'trafficSplitType'> = {
      cookieLifetime,
      trafficSplitType,
    }

    return {
      ...constantFields,

      ..._.omit(
        variableFields,
        fieldsToOmit<Variables>({
          cookieLifetime: !withYp2395,
          trafficSplitType: !withYp2395,
        }),
      ),
    }
  })

export const variantsToDTO = (
  variantsDTO: CreateAbTestInputData['variants'],
): Variables['variants'] => {
  if (variantsDTO === undefined) {
    throw new Error('Variants cannot be undefined')
  } else if (Object.keys(variantsDTO).length < 2) {
    throw new Error('At least two variants are required')
  }

  const variants: Variables['variants'] = {}

  Object.entries(variantsDTO).forEach(([variantKey, variantValue]) => {
    if (variantValue === undefined) return

    variants[variantKey as keyof Variables['variants']] = {
      ...variantValue,

      ...withDefined('general', variantValue?.general, general => ({
        ...general,
      })),

      ...withDefined('prebidStack', variantValue?.prebidStack, prebidStack => ({
        ...prebidStack,
        ...withDefined('bidders', prebidStack.bidders, regroupBiddersItems),
        ...withDefined('prebidModules', prebidStack.prebidModules, modules => ({
          ...withDefined('analytics', modules.analytics, regroupPrebidModulesItems),
          ...withDefined('general', modules.general, regroupPrebidModulesItems),
          ...withDefined('userId', modules.userId, regroupPrebidModulesItems),
          ...withDefined('vendorSpecific', modules.vendorSpecific, regroupPrebidModulesItems),
        })),
      })),
      ...withDefined('refresher', variantValue?.refresher, refresher => ({
        ...refresher,
        ...withDefined('basicSettings', refresher.basicSettings, basicSettings => ({
          ...basicSettings,
          ...withDefined(
            'desktopRefreshInterval',
            basicSettings.desktopRefreshInterval,
            secondsToMilliseconds,
          ),
          ...withDefined(
            'mobileRefreshInterval',
            basicSettings.mobileRefreshInterval,
            secondsToMilliseconds,
          ),
          ...withDefined('desktopAdThreshold', basicSettings.desktopAdThreshold, floatPercentToDTO),
          ...withDefined('mobileAdThreshold', basicSettings.mobileAdThreshold, floatPercentToDTO),
        })),
      })),
      ...withDefined('viewabilityTools', variantValue?.viewabilityTools, viewabilityTools => ({
        ...viewabilityTools,
        ...withDefined('lazyLoading', viewabilityTools.lazyLoading, lazyLoading => ({
          ...lazyLoading,
          ...withDefined(
            'gamAdRenderMargin',
            lazyLoading.gamAdRenderMargin,
            lazyLoading.lazyLoadType === 'yieldbirdLazyLoad' ? () => null : floatPercentToDTO,
          ),
          ...withDefined('gamAdRequestMargin', lazyLoading.gamAdRequestMargin, floatPercentToDTO),
          ...withDefined(
            'kvTargetingAndPrebidAuction',
            lazyLoading.kvTargetingAndPrebidAuction,
            lazyLoading.lazyLoadTargetingPrebidAuctionEnabled ? floatPercentToDTO : () => null,
          ),
        })),
      })),
      ...withDefined('trafficShare', variantValue?.trafficShare, floatPercentToDTO),
    } as PartialDeep<AbTestVariantAttributesDTO>
  })

  return variants
}

type WithDefined = <ObjectValue, CustomizerReturnType = ObjectValue>(
  key: string,
  value: ObjectValue | undefined,
  customizer?: (v: ObjectValue) => CustomizerReturnType,
) => {
  [x: string]: ((null | object) & ObjectValue) | CustomizerReturnType
}

/**
 * Conditionally adds a property to an object if value is not undefined, with optional customizer
 */
export const withDefined: WithDefined = (key, value, customizer) =>
  value !== undefined
    ? {
        [key]: customizer ? customizer(value) : value,
      }
    : {}

const regroupBiddersItems = (bidders: NonNullable<BiddersInput>): PrebidBidderInputDTO[] =>
  Object.entries(bidders).map(([id, { csEnabled, priority, s2sEnabled }]) => ({
    csEnabled,
    domainBidderId: id,
    priority,
    s2sEnabled,
  }))

const regroupPrebidModulesItems = (
  prebidModule: NonNullable<PrebidModuleInput>,
): PrebidModuleInputDTO[] =>
  Object.entries(prebidModule).map(([id, { enabled }]) => ({
    domainPrebidModuleId: id,
    enabled,
  }))
